import {
  type PendingRecoveryItem,
  type PreparedRecoveryEscrow,
  type RecoveryCaptureAdapter,
  type RecoveryCheckpoint,
  type RecoveryEscrowObjectDescriptor,
  type RecoveryEscrowTransport,
  type RecoveryIdentity,
  type RecoveryReceiptVerifier,
  type RecoveryRemoteObjectInfo,
  type RecoveryRemoteValidationGateway,
  type RecoveryState,
} from '@/types/recovery';
import { RecoveryGateService } from './RecoveryGateService';

const DEFAULT_MAX_PART_BYTES = 4 * 1024 * 1024;
const noCheckpoint: RecoveryCheckpoint = async () => undefined;

interface RecoveryEscrowServiceDependencies {
  gate: RecoveryGateService;
  capture: RecoveryCaptureAdapter;
  transport: RecoveryEscrowTransport;
  validationGateway: RecoveryRemoteValidationGateway;
  receiptVerifier: RecoveryReceiptVerifier;
  checkpoint?: RecoveryCheckpoint;
  maxPartBytes?: number;
}

const assertPreparedEscrow = (
  prepared: PreparedRecoveryEscrow,
  identity: RecoveryIdentity,
  recoveryId: string,
  maxPartBytes: number
): void => {
  const { manifest, manifestObject } = prepared;
  if (
    manifest.recoveryId !== recoveryId ||
    manifest.identity.companyId !== identity.companyId ||
    manifest.identity.userId !== identity.userId ||
    manifest.identity.deviceId !== identity.deviceId
  ) {
    throw new Error('Manifesto pertence a outra recuperacao.');
  }
  if (manifest.partCount !== manifest.parts.length || manifest.parts.length === 0) {
    throw new Error('Manifesto multipart inconsistente.');
  }
  const partIds = new Set<string>();
  const objectPaths = new Set<string>();
  manifest.parts.forEach((part, index) => {
    if (part.index !== index || partIds.has(part.partId) || objectPaths.has(part.objectPath)) {
      throw new Error('Ordem ou identidade de parte invalida.');
    }
    if (
      part.ciphertextBytes <= 0 ||
      part.ciphertextBytes > maxPartBytes ||
      !part.ciphertextSha512 ||
      !part.plaintextSha512 ||
      !part.localUri
    ) {
      throw new Error('Parte criptografada invalida.');
    }
    partIds.add(part.partId);
    objectPaths.add(part.objectPath);
  });
  for (const media of manifest.media) {
    if (
      !media.mediaId ||
      !media.sourceUri ||
      media.byteLength <= 0 ||
      !media.plaintextSha512 ||
      !media.contentType
    ) {
      throw new Error('Descritor de midia local invalido.');
    }
    const mediaPart = manifest.parts.some(
      part => part.kind === 'media' && part.mediaId === media.mediaId
    );
    if (!mediaPart) {
      throw new Error(`Midia local sem parte criptografada: ${media.mediaId}.`);
    }
  }
  for (const part of manifest.parts) {
    const mediaExists = manifest.media.some(media => media.mediaId === part.mediaId);
    if (
      (part.kind === 'database' && part.mediaId !== null) ||
      (part.kind === 'media' && (!part.mediaId || !mediaExists))
    ) {
      throw new Error(`Referencia de midia invalida na parte: ${part.partId}.`);
    }
  }
  if (
    !manifest.logicalSha512 ||
    manifest.totalCiphertextBytes !== manifest.parts.reduce(
      (total, part) => total + part.ciphertextBytes,
      0
    ) ||
    !manifestObject.objectPath ||
    !manifestObject.localUri ||
    manifestObject.ciphertextBytes <= 0 ||
    !manifestObject.ciphertextSha512 ||
    objectPaths.has(manifestObject.objectPath)
  ) {
    throw new Error('Manifesto criptografado invalido.');
  }
};

const remoteObjectMatches = (
  remote: RecoveryRemoteObjectInfo | null,
  expected: RecoveryEscrowObjectDescriptor
): boolean =>
  remote !== null &&
  remote.objectPath === expected.objectPath &&
  remote.ciphertextBytes === expected.ciphertextBytes &&
  (!remote.ciphertextSha512 || remote.ciphertextSha512 === expected.ciphertextSha512);

export class RecoveryEscrowService {
  private readonly gate: RecoveryGateService;
  private readonly capture: RecoveryCaptureAdapter;
  private readonly transport: RecoveryEscrowTransport;
  private readonly validationGateway: RecoveryRemoteValidationGateway;
  private readonly receiptVerifier: RecoveryReceiptVerifier;
  private readonly checkpoint: RecoveryCheckpoint;
  private readonly maxPartBytes: number;

  constructor(dependencies: RecoveryEscrowServiceDependencies) {
    this.gate = dependencies.gate;
    this.capture = dependencies.capture;
    this.transport = dependencies.transport;
    this.validationGateway = dependencies.validationGateway;
    this.receiptVerifier = dependencies.receiptVerifier;
    this.checkpoint = dependencies.checkpoint ?? noCheckpoint;
    this.maxPartBytes = dependencies.maxPartBytes ?? DEFAULT_MAX_PART_BYTES;
  }

  async prepareAndUpload(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    consentedAt: string;
    pendingItems: Array<Pick<PendingRecoveryItem, 'pendingId' | 'sourceDigest'>>;
  }): Promise<RecoveryState> {
    const { identity, recoveryId, consentedAt, pendingItems } = input;
    let state = await this.gate.beginRecovery({
      identity,
      recoveryId,
      consentedAt,
      pendingItems,
    });

    try {
      if (state.phase === 'capture_pending' || state.phase === 'capturing') {
        if (state.phase === 'capture_pending') {
          state = await this.gate.transition(identity, recoveryId, 'capturing');
        }
        await this.checkpoint({
          name: 'capture_started', identity, recoveryId, phase: state.phase,
        });
        const prepared = await this.capture.prepare({
          identity,
          recoveryId,
          createdAt: state.createdAt,
          maxPartBytes: this.maxPartBytes,
        });
        assertPreparedEscrow(prepared, identity, recoveryId, this.maxPartBytes);
        if (prepared.manifest.pendingItemCount !== pendingItems.length) {
          throw new Error('Contagem de pendencias diverge do manifesto.');
        }
        state = await this.gate.setPreparedEscrow(identity, recoveryId, {
          prepared,
          verifiedPartIds: [],
          manifestVerified: false,
          validationRequestedAt: null,
        });
        await this.checkpoint({
          name: 'capture_persisted', identity, recoveryId, phase: state.phase,
        });
      }

      if (state.phase === 'captured') {
        state = await this.gate.transition(identity, recoveryId, 'uploading');
      }

      if (state.phase === 'uploading') {
        if (!state.escrow) throw new Error('Escrow preparado ausente.');
        const prepared = state.escrow.prepared;
        await this.validationGateway.registerUpload({
          identity,
          recoveryId,
          prepared,
          idempotencyKey: `${recoveryId}:register-upload`,
        });
        for (const part of prepared.manifest.parts) {
          if (!state.escrow) throw new Error('Escrow preparado ausente.');
          if (!state.escrow.verifiedPartIds.includes(part.partId)) {
            await this.ensureRemoteObject(part);
            state = await this.gate.markPartVerified(identity, recoveryId, part.partId);
            await this.checkpoint({
              name: 'part_verified',
              identity,
              recoveryId,
              phase: state.phase,
              detailId: part.partId,
            });
          }
        }

        if (!state.escrow) throw new Error('Escrow preparado ausente.');
        if (!state.escrow.manifestVerified) {
          await this.ensureRemoteObject(prepared.manifestObject);
          state = await this.gate.markManifestVerified(identity, recoveryId);
          await this.checkpoint({
            name: 'manifest_verified', identity, recoveryId, phase: state.phase,
          });
        }

        if (!state.escrow) throw new Error('Escrow preparado ausente.');
        if (!state.escrow.validationRequestedAt) {
          await this.validationGateway.requestValidation({
            identity,
            recoveryId,
            prepared,
            idempotencyKey: `${recoveryId}:request-validation`,
          });
          state = await this.gate.markValidationRequested(identity, recoveryId);
          await this.checkpoint({
            name: 'validation_requested', identity, recoveryId, phase: state.phase,
          });
        }
      }

      return state;
    } catch (error) {
      await this.gate.recordError(identity, recoveryId, error);
      throw error;
    }
  }

  async refreshRemoteReceipt(
    identity: RecoveryIdentity,
    recoveryId: string
  ): Promise<RecoveryState> {
    const state = await this.gate.getState(identity);
    if (!state || state.recoveryId !== recoveryId) {
      throw new Error('Recuperacao ativa nao encontrada.');
    }
    if (state.validatedReceipt) return state;
    if (state.phase !== 'awaiting_validation') {
      throw new Error('Recuperacao ainda nao aguarda validacao remota.');
    }
    if (!state.escrow) throw new Error('Escrow preparado ausente.');

    try {
      const receipt = await this.validationGateway.fetchReceipt({ identity, recoveryId });
      if (!receipt) return state;
      const verification = await this.receiptVerifier.verify(receipt, {
        identity,
        recoveryId,
        manifestSha512: state.escrow.prepared.manifestObject.ciphertextSha512,
      });
      if (!verification.valid) {
        throw new Error(verification.reason ?? 'Receipt remoto rejeitado.');
      }
      const persisted = await this.gate.recordValidatedReceipt(identity, recoveryId, {
        receipt,
        verification: { ...verification, valid: true },
      });
      await this.checkpoint({
        name: 'receipt_persisted', identity, recoveryId, phase: persisted.phase,
      });
      return persisted;
    } catch (error) {
      await this.gate.recordError(identity, recoveryId, error);
      throw error;
    }
  }

  private async ensureRemoteObject(object: RecoveryEscrowObjectDescriptor): Promise<void> {
    const existing = await this.transport.inspectObject(object.objectPath);
    if (existing) {
      if (!remoteObjectMatches(existing, object)) {
        throw new Error(`Objeto remoto divergente: ${object.objectPath}.`);
      }
      return;
    }

    await this.transport.uploadImmutable(object);
    const uploaded = await this.transport.inspectObject(object.objectPath);
    if (!remoteObjectMatches(uploaded, object)) {
      throw new Error(`Upload remoto nao confirmado: ${object.objectPath}.`);
    }
  }
}
